import { useCallback, useState } from "react";
import { getGeocode } from "use-places-autocomplete";
import {
  PeliasResult,
  peliasReverseGeocode,
} from "../../utils/APICalls/peliasAPI";
import { setSelectedAddress } from "../redux/slices/deliverySlice";
import {
  LoadingActionEnum,
  decreasePendingOperations,
  increasePendingOperations,
  pushToLoadingStack,
  setLoading,
} from "../redux/slices/globalComponentsToggleSlice";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";
import { useTranslationI18 } from "./useTranslationI18";

// Types
interface GeolocationError {
  code: number;
  message: string;
}

interface GeolocationPosition {
  coords: {
    latitude: number;
    longitude: number;
  };
}

interface UseDetectCurrentLocationOptions {
  onCenterChange?: (center: { lat: number; lng: number }) => void;
  onError?: (error: { text: string; success?: boolean } | null) => void;
  onErrorStateChange?: (
    error: { text: string; success?: boolean } | null
  ) => void; // Alias for onError
  onAddressSelect?: (address: {
    description: string;
    place_id: string;
  }) => void;
  onValueSet?: (value: string, shouldFetchData?: boolean) => void;
  functionName?: string;
}

interface UseDetectCurrentLocationReturn {
  getCurrentPosition: () => void;
  isLoading: boolean;
  error: { text: string; success?: boolean } | null;
  clearError: () => void;
}

export const useDetectCurrentLocation = (
  options: UseDetectCurrentLocationOptions = {}
): UseDetectCurrentLocationReturn => {
  const dispatch = useAppDispatch();
  const { t } = useTranslationI18();
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantInfo?.config
  );
  const enableInternalMaps = restaurantConfig?.enableInternalMaps ?? false;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<{
    text: string;
    success?: boolean;
  } | null>(null);

  const {
    onCenterChange,
    onError,
    onErrorStateChange,
    onAddressSelect,
    onValueSet,
    functionName = "detectCurrentLocation",
  } = options;

  // Use onErrorStateChange as fallback for onError
  const handleError = onError || onErrorStateChange;

  const translations = {
    enableLocation: t("delivery:enable-location"),
    connectionTimeout: t("delivery:connection-timeout"),
  };

  const handleLoadingState = useCallback(
    (loading: boolean) => {
      setIsLoading(loading);

      if (restaurantConfig?.loadingStateSolution) {
        const action = loading
          ? LoadingActionEnum.INCREASE
          : LoadingActionEnum.DECREASE;
        const fullFunctionName = `${functionName} ${
          loading ? "" : "success "
        }getCurrentPosition`;

        dispatch(
          pushToLoadingStack({
            action,
            functionName: fullFunctionName,
            date: new Date(),
          })
        );

        if (loading) {
          dispatch(increasePendingOperations());
        } else {
          dispatch(decreasePendingOperations());
        }
      } else {
        dispatch(setLoading(loading));
      }
    },
    [dispatch, restaurantConfig, functionName]
  );

  const handleGeolocationSuccess = useCallback(
    async (pos: GeolocationPosition) => {
      handleLoadingState(false);
      setError(null);

      const crd = pos.coords;
      const newCenter = { lat: crd.latitude, lng: crd.longitude };

      onCenterChange?.(newCenter);

      if (enableInternalMaps) {
        try {
          const result: PeliasResult = await peliasReverseGeocode({
            point: {
              lat: crd.latitude,
              lon: crd.longitude,
            },
          });

          const peliasCenter = {
            lat: parseFloat(result.lat),
            lng: parseFloat(result.lon),
          };
          onCenterChange?.(peliasCenter);
          onValueSet?.(result.display_name, false);
        } catch (error) {
          console.error("Pelias reverse geocoding error:", error);
          setError({ text: "Failed to get address details" });
        }
      } else {
        try {
          const res = await getGeocode({
            location: { lat: crd.latitude, lng: crd.longitude },
          });

          onAddressSelect?.({
            description: res[0].formatted_address,
            place_id: res[0].place_id,
          });
        } catch (error) {
          console.error("Google Maps geocoding error:", error);
          setError({ text: "Failed to get address details" });
        }
      }
    },
    [
      handleLoadingState,
      onCenterChange,
      onValueSet,
      onAddressSelect,
      enableInternalMaps,
    ]
  );

  const getCurrentPosition = useCallback(
    (tries: number = 0) => {
      handleLoadingState(true);
      dispatch(setSelectedAddress(null));
      setError(null);

      const handleSuccess = (position: GeolocationPosition) => {
        handleGeolocationSuccess(position);
      };

      const handleGeolocationError = (err: GeolocationError) => {
        if (tries < 2) {
          return getCurrentPosition(tries + 1);
        }

        handleLoadingState(false);

        let errorMessage: string;
        switch (err.code) {
          case 1:
            errorMessage = translations.enableLocation;
            break;
          case 3:
            errorMessage = translations.connectionTimeout;
            break;
          default:
            errorMessage = `ERROR(${err.code}): ${err.message}`;
            break;
        }

        const errorObj = { text: errorMessage };
        setError(errorObj);
        handleError?.(errorObj);

        console.warn(`ERROR(${err.code}): ${err.message}`);
      };

      navigator.geolocation.getCurrentPosition(
        handleSuccess,
        handleGeolocationError,
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    },
    [handleLoadingState, dispatch, handleGeolocationSuccess]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    getCurrentPosition: () => getCurrentPosition(0),
    isLoading,
    error,
    clearError,
  };
};
